import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const servicesRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  const linkBase =
    "text-white/90 font-medium tracking-wide transition-colors hover:text-brand-green";
  const activeLink = "text-brand-green font-semibold";
  const isActive = (p) => location.pathname === p;

  return (
    <header
      className="sticky top-0 z-50 bg-brand-dark-blue text-white shadow-[0_1px_0_0_rgba(255,255,255,0.06)]"
      style={{ backgroundColor: "#051C2F" }}
    >
      <div className="mx-auto max-w-7xl px-4 md:px-6 bg-brand-dark-blue">
        <div className="flex items-center justify-between h-16 md:h-20">
          <button
            onClick={() => navigate("/")}
            className="shrink-0"
          >
            <img src="/logo192.png" alt="Kyra Logo" className="h-14 md:h-16" />
          </button>

          <nav className="hidden md:flex items-center gap-8">
            <button
              onClick={() => navigate("/")}
              className={`${linkBase} ${isActive("/") ? activeLink : ""}`}
            >
              Home
            </button>
            <button
              onClick={() => navigate("/about")}
              className={`${linkBase} ${isActive("/about") ? activeLink : ""}`}
            >
              About Us
            </button>

            {/* Services dropdown */}
            <div className="relative" ref={servicesRef}>
              <button
                type="button"
                onClick={() => setServicesOpen((p) => !p)}
                className={`${linkBase} inline-flex items-center gap-1`}
                aria-expanded={servicesOpen}
                aria-haspopup="menu"
              >
                Services
                <svg
                  className={`w-4 h-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {servicesOpen && (
                <div
                  className="absolute left-0 mt-2 w-56 bg-brand-dark-blue shadow-lg overflow-hidden"
                  role="menu"
                >
                  <button
                    onClick={() => navigate("/services/solar-panel")}
                    className="block w-full text-left px-4 py-2.5 text-sm text-white/90 hover:text-brand-green"
                  >
                    Solar Panel
                  </button>
                  <button
                    onClick={() => navigate("/services/solar-inverter")}
                    className="block w-full text-left px-4 py-2.5 text-sm text-white/90 hover:text-brand-green"
                  >
                    Solar Inverter
                  </button>
                  <button
                    onClick={() => navigate("/services/energy-storage-systems")}
                    className="block w-full text-left px-4 py-2.5 text-sm text-white/90 hover:text-brand-green"
                  >
                    Energy Storage Systems (ESS)
                  </button>
                  <button
                    onClick={() => navigate("/services/solar-e-vehicle")}
                    className="block w-full text-left px-4 py-2.5 text-sm text-white/90 hover:text-brand-green"
                  >
                    Solar E-Vehicle
                  </button>
                  <button
                    onClick={() => navigate("/services/solar-epc-services")}
                    className="block w-full text-left px-4 py-2.5 text-sm text-white/90 hover:text-brand-green"
                  >
                    Solar EPC Services
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={() => navigate("/contact")}
              className={`${linkBase} ${isActive("/contact") ? activeLink : ""}`}
            >
              Contact Us
            </button>
          </nav>

          <div className="hidden md:block">
            <button
              onClick={() => navigate("/contact")}
              className="inline-flex items-center gap-2 rounded-lg bg-brand-green px-5 py-3 text-white font-medium shadow hover:bg-green-700 transition"
            >
              Get Quote
              <svg className="w-4 h-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 10h10M11 6l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          <button
            className="md:hidden inline-flex items-center justify-center rounded-md p-2 text-white/90 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/20"
            onClick={() => setIsOpen((p) => !p)}
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {isOpen && (
          <div className="md:hidden pb-4 bg-brand-dark-blue transition-all duration-300">
            <nav className="flex flex-col gap-1 pt-2">
              <button
                onClick={() => navigate("/")}
                className={`px-4 py-2 rounded-md text-left ${linkBase} ${isActive("/") ? "bg-white/5 text-brand-green" : ""}`}
              >
                Home
              </button>
              <button
                onClick={() => navigate("/about")}
                className={`px-4 py-2 rounded-md text-left ${linkBase} ${isActive("/about") ? "bg-white/5 text-brand-green" : ""}`}
              >
                About Us
              </button>

              {/* Direct Service Items (no submenu) */}
              <button
                onClick={() => { navigate("/services/solar-panel"); setIsOpen(false); }}
                className="px-4 py-2 text-left text-white/90 hover:text-brand-green rounded-md"
              >
                Solar Panel
              </button>
              <button
                onClick={() => { navigate("/services/solar-inverter"); setIsOpen(false); }}
                className="px-4 py-2 text-left text-white/90 hover:text-brand-green rounded-md"
              >
                Solar Inverter
              </button>
              <button
                onClick={() => { navigate("/services/energy-storage-systems"); setIsOpen(false); }}
                className="px-4 py-2 text-left text-white/90 hover:text-brand-green rounded-md"
              >
                Energy Storage Systems (ESS)
              </button>
              <button
                onClick={() => { navigate("/services/solar-e-vehicle"); setIsOpen(false); }}
                className="px-4 py-2 text-left text-white/90 hover:text-brand-green rounded-md"
              >
                Solar E-Vehicle
              </button>
              <button
                onClick={() => { navigate("/services/solar-epc-services"); setIsOpen(false); }}
                className="px-4 py-2 text-left text-white/90 hover:text-brand-green rounded-md"
              >
                Solar EPC Services
              </button>

              <button
                onClick={() => navigate("/contact")}
                className={`px-4 py-2 rounded-md text-left ${linkBase} ${isActive("/contact") ? "bg-white/5 text-brand-green" : ""}`}
              >
                Contact Us
              </button>

              <button
                onClick={() => navigate("/contact")}
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-lg bg-brand-green px-4 py-3 text-white font-medium shadow hover:bg-green-700 transition"
              >
                Get Quote
                <svg className="w-4 h-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 10h10M11 6l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </nav>
          </div>
        )}

      </div>
    </header>
  );
};

export default Navbar;
